/**
 * useResponsive Composable
 *
 * @version 1.0.0
 * @description Typed wrapper around Vuetify's useDisplay() providing semantic
 *              breakpoint detection, device classification, and touch detection.
 *              Single source of truth for responsive logic across the entire app.
 *
 * REPLACES:
 * - useAppState().isMobile (regex-based UA sniffing → Vuetify viewport detection)
 * - Scattered useDisplay() calls in individual components
 *
 * BREAKPOINT RANGES (aligned with Vuetify 3 thresholds):
 *   isMobile:  width < 600px   (xs)
 *   isTablet:  600px ≤ width < 960px (sm)
 *   isDesktop: width ≥ 960px   (md+)
 *
 * USAGE:
 *   const { isMobile, isTablet, isDesktop, breakpoint, isTouchDevice } = useResponsive()
 *
 *   // In template:
 *   <div v-if="isMobile">Mobile layout</div>
 *   <div v-else-if="isTablet">Tablet layout</div>
 *   <div v-else>Desktop layout</div>
 *
 *   // Responsive prop:
 *   <v-dialog :fullscreen="isMobile" />
 *
 * @see https://vuetifyjs.com/en/composables/display/
 */

import { computed, ref, onMounted, onUnmounted, type ComputedRef, type Ref } from 'vue'
import { useDisplay } from 'vuetify'

// =============================================================================
// Types
// =============================================================================

/** Semantic breakpoint name */
export type BreakpointName = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl'

/** Device classification based on viewport width */
export type DeviceClass = 'mobile' | 'tablet' | 'desktop'

/** Orientation */
export type Orientation = 'portrait' | 'landscape'

export interface UseResponsiveReturn {
  // ── Device Classification ──────────────────────────────────────────────
  /** true when viewport < 600px (xs) */
  readonly isMobile: ComputedRef<boolean>
  /** true when 600px ≤ viewport < 960px (sm) */
  readonly isTablet: ComputedRef<boolean>
  /** true when viewport ≥ 960px (md+) */
  readonly isDesktop: ComputedRef<boolean>
  /** true when viewport < 960px (xs or sm) */
  readonly isMobileOrTablet: ComputedRef<boolean>

  /** Semantic device class: 'mobile' | 'tablet' | 'desktop' */
  readonly deviceClass: ComputedRef<DeviceClass>

  // ── Raw Breakpoint ─────────────────────────────────────────────────────
  /** Current Vuetify breakpoint name: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl' */
  readonly breakpoint: ComputedRef<BreakpointName>

  /** Viewport width in pixels */
  readonly viewportWidth: ComputedRef<number>
  /** Viewport height in pixels */
  readonly viewportHeight: ComputedRef<number>

  // ── Touch & Input ──────────────────────────────────────────────────────
  /** true when primary input is coarse (touch screen) — detected via matchMedia */
  readonly isTouchDevice: Ref<boolean>

  // ── Orientation ────────────────────────────────────────────────────────
  /** Current device orientation */
  readonly orientation: ComputedRef<Orientation>

  // ── Helpers ────────────────────────────────────────────────────────────
  /** Returns true if viewport width is at or above the given breakpoint */
  isAbove: (bp: BreakpointName) => boolean
  /** Returns true if viewport width is below the given breakpoint */
  isBelow: (bp: BreakpointName) => boolean
}

// =============================================================================
// Breakpoint Thresholds (must match Vuetify config in plugins/vuetify.ts)
// =============================================================================

const BP_THRESHOLDS: Record<BreakpointName, number> = {
  xs: 0,
  sm: 600,
  md: 960,
  lg: 1280,
  xl: 1920,
  xxl: 2560,
}

// =============================================================================
// Composable
// =============================================================================

export function useResponsive(): UseResponsiveReturn {
  // ── Vuetify Display API ────────────────────────────────────────────────
  const display = useDisplay()

  // ── Device Classification ──────────────────────────────────────────────

  const isMobile = computed<boolean>(() => display.xs.value)

  const isTablet = computed<boolean>(() => display.sm.value)

  const isDesktop = computed<boolean>(() => display.mdAndUp.value)

  const isMobileOrTablet = computed<boolean>(() => display.smAndDown.value)

  const deviceClass = computed<DeviceClass>(() => {
    if (display.xs.value) return 'mobile'
    if (display.sm.value) return 'tablet'
    return 'desktop'
  })

  // ── Raw Breakpoint ─────────────────────────────────────────────────────

  const breakpoint = computed<BreakpointName>(() => display.name.value as BreakpointName)

  const viewportWidth = computed<number>(() => display.width.value)
  const viewportHeight = computed<number>(() => display.height.value)

  // ── Touch Detection ────────────────────────────────────────────────────
  // Uses matchMedia('(pointer: coarse)') for accurate detection.
  // Falls back to navigator.maxTouchPoints for older browsers.

  const isTouchDevice = ref<boolean>(false)
  let touchMediaQuery: MediaQueryList | null = null

  function updateTouchDetection(): void {
    if (typeof window === 'undefined') return

    if (window.matchMedia) {
      const mq = window.matchMedia('(pointer: coarse)')
      isTouchDevice.value = mq.matches
    } else {
      // Fallback for very old browsers
      isTouchDevice.value = navigator.maxTouchPoints > 0
    }
  }

  function handleTouchMediaChange(e: MediaQueryListEvent): void {
    isTouchDevice.value = e.matches
  }

  onMounted(() => {
    updateTouchDetection()

    // Listen for changes (e.g., desktop switching to tablet mode)
    if (typeof window !== 'undefined' && window.matchMedia) {
      touchMediaQuery = window.matchMedia('(pointer: coarse)')
      touchMediaQuery.addEventListener('change', handleTouchMediaChange)
    }
  })

  onUnmounted(() => {
    if (touchMediaQuery) {
      touchMediaQuery.removeEventListener('change', handleTouchMediaChange)
      touchMediaQuery = null
    }
  })

  // ── Orientation ────────────────────────────────────────────────────────

  const orientation = computed<Orientation>(() => {
    return display.width.value >= display.height.value ? 'landscape' : 'portrait'
  })

  // ── Helpers ────────────────────────────────────────────────────────────

  /**
   * Returns true if the current viewport width is at or above the given breakpoint.
   * Example: isAbove('md') → true when width ≥ 960px
   */
  function isAbove(bp: BreakpointName): boolean {
    return display.width.value >= BP_THRESHOLDS[bp]
  }

  /**
   * Returns true if the current viewport width is below the given breakpoint.
   * Example: isBelow('md') → true when width < 960px
   */
  function isBelow(bp: BreakpointName): boolean {
    return display.width.value < BP_THRESHOLDS[bp]
  }

  // ── Return ─────────────────────────────────────────────────────────────

  return {
    isMobile,
    isTablet,
    isDesktop,
    isMobileOrTablet,
    deviceClass,
    breakpoint,
    viewportWidth,
    viewportHeight,
    isTouchDevice,
    orientation,
    isAbove,
    isBelow,
  }
}

export default useResponsive
